import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Rocket, LogOut, Search, Users, TrendingUp, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const STATUSES = ["new", "in_review", "approved", "funded", "declined"];
const STATUS_STYLES = {
  new: "bg-blue-100 text-blue-700",
  in_review: "bg-amber-100 text-amber-700",
  approved: "bg-purple-100 text-purple-700",
  funded: "bg-green-100 text-green-700",
  declined: "bg-slate-200 text-slate-600",
};

export default function AdminDashboard() {
  const { user, checked, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (checked && !user) navigate("/admin/login");
  }, [checked, user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [ls, st] = await Promise.all([
        api.get("/admin/leads"),
        api.get("/admin/stats"),
      ]);
      setLeads(ls.data);
      setStats(st.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") load();
  }, [user]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/leads/${id}`, { status });
      setLeads((cur) => cur.map((l) => (l.id === id ? data : l)));
      if (selected?.id === id) setSelected(data);
      toast.success("Status updated");
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (statusFilter !== "all" && l.status !== statusFilter) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      return (
        l.full_name?.toLowerCase().includes(s) ||
        l.email?.toLowerCase().includes(s) ||
        l.business_name?.toLowerCase().includes(s) ||
        l.phone?.toLowerCase().includes(s)
      );
    });
  }, [leads, q, statusFilter]);

  if (!checked || !user) {
    return (
      <div className="min-h-screen grid place-items-center text-slate-400">
        Loading…
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-[#0b1121] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="grid place-items-center w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-600 shadow-lg shadow-purple-500/40">
              <Rocket className="w-4 h-4 text-white" />
            </span>
            <span className="font-display font-extrabold">ROCKETCAP</span>
            <span className="text-white/40 text-sm ml-3">/ Admin</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-xs text-white/60">{user.email}</span>
            <button
              data-testid="admin-logout"
              onClick={async () => {
                await logout();
                navigate("/admin/login");
              }}
              className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total leads" value={stats?.leads_total ?? 0} icon={Users} accent="purple" />
          <StatCard label="New" value={stats?.leads_new ?? 0} icon={Clock} accent="blue" />
          <StatCard label="Approved" value={stats?.leads_approved ?? 0} icon={TrendingUp} accent="purple" />
          <StatCard label="Funded" value={stats?.leads_funded ?? 0} icon={CheckCircle2} accent="green" />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4 flex flex-col md:flex-row gap-3 md:items-center">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="admin-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, phone, business…"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500"
            />
          </div>
          <select
            data-testid="admin-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-purple-500/30"
          >
            <option value="all">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s.replace("_", " ")}</option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Name</th>
                  <th className="text-left px-5 py-3 font-semibold">Business</th>
                  <th className="text-left px-5 py-3 font-semibold">Email</th>
                  <th className="text-left px-5 py-3 font-semibold">Phone</th>
                  <th className="text-left px-5 py-3 font-semibold">Amount</th>
                  <th className="text-left px-5 py-3 font-semibold">Status</th>
                  <th className="text-left px-5 py-3 font-semibold">When</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-16 text-center text-slate-400">
                      Loading leads…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-16 text-center text-slate-400">
                      No leads match your filters.
                    </td>
                  </tr>
                ) : (
                  filtered.map((l) => (
                    <tr
                      key={l.id}
                      onClick={() => setSelected(l)}
                      data-testid={`lead-row-${l.id}`}
                      className="border-t border-slate-100 hover:bg-purple-50/40 cursor-pointer"
                    >
                      <td className="px-5 py-4 font-semibold text-slate-900">{l.full_name}</td>
                      <td className="px-5 py-4 text-slate-600">{l.business_name || "—"}</td>
                      <td className="px-5 py-4 text-slate-600">{l.email}</td>
                      <td className="px-5 py-4 text-slate-600">{l.phone}</td>
                      <td className="px-5 py-4 font-semibold text-purple-700">
                        ${(l.funding_amount || 0).toLocaleString()}
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${
                            STATUS_STYLES[l.status]
                          }`}
                        >
                          {l.status.replace("_", " ")}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-slate-400 text-xs">
                        {new Date(l.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Detail drawer */}
      {selected && (
        <div
          className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm flex justify-end"
          onClick={() => setSelected(null)}
        >
          <div
            className="w-full max-w-md h-full bg-white shadow-2xl overflow-y-auto p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="lead-detail-drawer"
          >
            <button
              onClick={() => setSelected(null)}
              className="text-xs uppercase tracking-widest text-slate-400 hover:text-slate-800 font-semibold mb-6"
            >
              ← Close
            </button>
            <h2 className="font-display text-2xl font-black text-slate-900">
              {selected.full_name}
            </h2>
            <p className="text-slate-500 text-sm">{selected.business_name}</p>

            <div className="mt-6 space-y-3 text-sm">
              <Row k="Email" v={selected.email} />
              <Row k="Phone" v={selected.phone} />
              <Row k="Business type" v={selected.business_type} />
              <Row k="Time in business" v={selected.time_in_business} />
              <Row k="Monthly revenue" v={`$${(selected.monthly_revenue || 0).toLocaleString()}`} />
              <Row k="Credit score" v={selected.credit_score} />
              <Row k="Requested amount" v={`$${(selected.funding_amount || 0).toLocaleString()}`} />
              <Row k="Purpose" v={selected.funding_purpose || "—"} />
              {selected.estimated_range_low ? (
                <Row
                  k="Est. estimator range"
                  v={`$${selected.estimated_range_low.toLocaleString()} – $${selected.estimated_range_high.toLocaleString()}`}
                />
              ) : null}
              <Row k="Notes" v={selected.notes || "—"} />
              <Row k="Received" v={new Date(selected.created_at).toLocaleString()} />
            </div>

            <div className="mt-8">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                Update status
              </p>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    data-testid={`status-btn-${s}`}
                    onClick={() => updateStatus(selected.id, s)}
                    className={`rounded-full text-xs font-bold uppercase tracking-wide px-3 py-1.5 border transition ${
                      selected.status === s
                        ? "bg-purple-600 text-white border-purple-600"
                        : "bg-white text-slate-600 border-slate-200 hover:border-purple-400"
                    }`}
                  >
                    {s.replace("_", " ")}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  const accentCls =
    {
      purple: "bg-purple-100 text-purple-700",
      blue: "bg-blue-100 text-blue-700",
      green: "bg-green-100 text-green-700",
    }[accent] || "bg-slate-100 text-slate-700";
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          {label}
        </p>
        <span className={`w-8 h-8 rounded-lg grid place-items-center ${accentCls}`}>
          <Icon className="w-4 h-4" />
        </span>
      </div>
      <p className="mt-3 font-display text-3xl font-black text-slate-900">{value}</p>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3 border-b border-slate-100 pb-2">
      <span className="text-slate-400 text-xs uppercase tracking-wider font-semibold">{k}</span>
      <span className="text-slate-800 font-medium text-right break-words max-w-[60%]">{v}</span>
    </div>
  );
}
